
import { defineComponent } from 'vue';
import { ActionTypes } from '@/store/actionTypes';
import { MutationTypes } from '@/store/mutationTypes';
import FbxButton from '@/components/fbx-button.vue';
import FbxInputReset from '@/components/fbx-input-reset.vue';
import FbaApiError from '@/types/FbaApiError';
import globalConstants from '@/shared/globalConstants';
import { mapGetters } from 'vuex';

const DEFAULT_PARENT_CLASSES = ['uix-control', 'uix-text-control'];
export default defineComponent({
    name: 'TrailerLookup',
    emits: ['onSearchTrailer', 'onToggleScanner'],
    components: {
        FbxButton,
        FbxInputReset,
    },
    data: (): {
        errorText: string;
        errorTextClass: string;
        extraClass: string;
        hasAlert: boolean;
        isInputText: boolean;
        maxLength: number;
        trailerId: string | '';
    } => {
        return {
            errorText: '',
            errorTextClass: '',
            extraClass: DEFAULT_PARENT_CLASSES.join(' '),
            hasAlert: false,
            isInputText: false,
            maxLength: 6,
            trailerId: '',
        };
    },
    props: {
        scannerButtonEnabled: Boolean,
        showCameraSettings: Boolean,
        headLineText: String,
    },
    mounted() {
        this.$store.dispatch(ActionTypes.CLEAR_ERROR, undefined);
    },
    computed: {
        ...mapGetters({
            trailer: 'selectedTrailer',
        }),
        error(): FbaApiError | undefined {
            return this.$store.getters.getError;
        },
        showAlert(): boolean {
            return (
                this.hasAlert &&
                this.error?.reasonCode !==
                    globalConstants.reasonCodes.trailerNotFound
            );
        },
        driverCompany(): string {
            return this.$route.params.company as string;
        },
    },
    methods: {
        onClear() {
            this.trailerId = '';
            this.$store.dispatch(ActionTypes.CLEAR_ERROR, undefined);
            this.resetValidations();
        },
        openScanner() {
            this.$emit('onToggleScanner', true);
        },
        scrollInputIntoView(): void {
            const trailerCard = document.getElementById('trailer-input');
            if (trailerCard)
                trailerCard.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
        },
        resetValidations(): void {
            this.hasAlert = false;
            this.errorText = '';
            this.errorTextClass = '';
            this.extraClass = DEFAULT_PARENT_CLASSES.join(' ');
        },
        validateForm(): boolean {
            if (!this.trailerId || this.trailerId.toUpperCase() === 'SWFZ') {
                this.hasAlert = true;
                this.errorText = 'Not found!';
                this.errorTextClass = 'uix-error-text';
                this.extraClass =
                    DEFAULT_PARENT_CLASSES.join(' ') + ' has-error';
                return false;
            } else {
                this.extraClass = DEFAULT_PARENT_CLASSES.join(' ');
                this.hasAlert = false;
            }

            return true;
        },
        onCameraSettingsClick() {
            this.$store.commit(
                MutationTypes.SET_PERMISSIONS_STEPS_GUIDE_SHOWN,
                true
            );
        },
        async onSubmit() {
            if (this.trailerId === '') return;

            this.$store.dispatch(ActionTypes.CLEAR_ERROR, undefined);
            this.resetValidations();

            if (this.validateForm()) {
                this.$emit('onSearchTrailer', this.trailerId);
            }
        },
    },
    watch: {
        error(newValue) {
            if (newValue && this.trailerId != '') {
                this.hasAlert = true;
                this.errorText = 'Not found!';
                this.errorTextClass = 'uix-error-text';
                this.extraClass =
                    DEFAULT_PARENT_CLASSES.join(' ') + ' has-error';
            }
        },
        trailerId(newValue) {
            if (newValue.toUpperCase().startsWith('SWFZ')) {
                this.maxLength = 10;
            } else {
                this.maxLength = 6;
            }

            if (/[^\w_]/g.test(newValue)) {
                this.hasAlert = true;
                this.errorText = 'Invalid Trailer Number';
                this.errorTextClass = 'uix-error-text';
                this.extraClass =
                    DEFAULT_PARENT_CLASSES.join(' ') + ' has-error';
            } else {
                this.resetValidations();
            }
        },
        trailer() {
            if (this.trailer) this.onClear();
        },
    },
});
